import DOMPurify from "dompurify";
import { marked } from "marked";

/**
 * Turns one manual chapter's Markdown into HTML that is safe to drop into the
 * DOM and wired into the app rather than the raw web.
 *
 * Three things happen to the Markdown besides parsing it:
 *
 *  - Every heading gets a stable `id` (a slug of its text), so an anchor like
 *    `budgets.md#overspend` has something to land on.
 *  - A chapter link (`budgets.md`, `budgets.md#overspend`) becomes an in-app
 *    route (`<manualBasePath>/budgets#overspend`) and is marked
 *    `data-manual-link` so `ManualPage` can intercept the click and navigate
 *    through the router instead of reloading the page. An external `http(s)`
 *    link is left alone except for `target="_blank"` and
 *    `rel="noopener noreferrer"`.
 *  - A relative image (`images/budgets.png`) loses its `src` — an unauthenticated
 *    `<img src>` would just render the API's 401 — and gains
 *    `data-manual-src` with the path resolved against the chapter's own
 *    directory, for the page to load through the authorised fetch and turn
 *    into an object URL.
 *
 * A relative path that would escape the language root (`../../etc/passwd`,
 * say) is refused rather than turned into a link or an image source: the
 * manual is our own content today, but "trusted content" is not a property
 * this function should get to assume forever.
 *
 * The HTML is sanitised twice: once right after `marked` parses it (the
 * manual is meant to be plain CommonMark with no raw HTML, but a sanitiser
 * that trusted that would not be defence in depth), and once more after the
 * rewriting above, since that is the version that actually reaches the DOM.
 */
export interface RenderManualOptions {
  /** The chapter this Markdown came from, e.g. `"budgets.md"` or `"toc.md"`. */
  currentPath: string;
  /** The in-app route chapter links resolve into: `/households/<id>/manual`. */
  manualBasePath: string;
}

const EXTERNAL_LINK = /^https?:\/\//i;
const CHAPTER_LINK = /\.md(?:#.*)?$/i;

const SANITIZE_OPTIONS = {
  ADD_ATTR: ["target", "data-manual-link", "data-manual-src"],
};

export function renderManual(markdown: string, options: RenderManualOptions): string {
  const rawHtml = marked.parse(markdown, { async: false, gfm: true }) as string;
  const firstPass = DOMPurify.sanitize(rawHtml, SANITIZE_OPTIONS);

  const doc = new DOMParser().parseFromString(firstPass, "text/html");
  addHeadingIds(doc);
  rewriteLinks(doc, options);
  rewriteImages(doc, options);

  return DOMPurify.sanitize(doc.body.innerHTML, SANITIZE_OPTIONS);
}

/**
 * Resolves a Markdown-relative path against the directory of the chapter that
 * references it — `images/x.png` from `dashboard.md` resolves to
 * `images/x.png`; `../en/dashboard.md` resolves to nothing, because there is
 * no `..` to take from the language root.
 *
 * Returns `null` for a path that would climb above the root, rather than the
 * closest path that does not — a chapter that got its relative link wrong
 * should show a dead link, not a guess at what it meant.
 */
export function resolveManualPath(currentPath: string, relative: string): string | null {
  const slashIndex = currentPath.lastIndexOf("/");
  const dir = slashIndex === -1 ? "" : currentPath.slice(0, slashIndex);
  const combined = dir ? `${dir}/${relative}` : relative;

  const stack: string[] = [];
  for (const part of combined.split("/")) {
    if (part === "" || part === ".") continue;
    if (part === "..") {
      if (stack.length === 0) return null;
      stack.pop();
    } else {
      stack.push(part);
    }
  }
  return stack.join("/");
}

/** A URL-safe, readable id for a heading: "Am I okay?" → "am-i-okay". */
export function slugify(text: string): string {
  const slug = text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "section";
}

function addHeadingIds(doc: Document): void {
  const used = new Set<string>();
  doc.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach((heading) => {
    if (heading.id) {
      used.add(heading.id);
      return;
    }
    const base = slugify(heading.textContent ?? "");
    let id = base;
    let suffix = 2;
    while (used.has(id)) {
      id = `${base}-${suffix}`;
      suffix += 1;
    }
    used.add(id);
    heading.id = id;
  });
}

function rewriteLinks(doc: Document, options: RenderManualOptions): void {
  doc.querySelectorAll("a[href]").forEach((anchor) => {
    const href = anchor.getAttribute("href");
    if (!href) return;

    if (EXTERNAL_LINK.test(href)) {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
      return;
    }

    if (!CHAPTER_LINK.test(href)) return;

    const hashIndex = href.indexOf("#");
    const filePart = hashIndex === -1 ? href : href.slice(0, hashIndex);
    const fragment = hashIndex === -1 ? "" : href.slice(hashIndex + 1);

    const resolved = resolveManualPath(options.currentPath, filePart);
    if (resolved === null) {
      // Escapes the language root: refuse to turn it into a route. Left
      // without an `href`, it renders as inert text rather than a link that
      // looks clickable but goes nowhere real.
      anchor.removeAttribute("href");
      return;
    }

    const slug = resolved.replace(/\.md$/i, "");
    anchor.setAttribute(
      "href",
      `${options.manualBasePath}/${slug}${fragment ? `#${fragment}` : ""}`,
    );
    anchor.setAttribute("data-manual-link", "true");
  });
}

function rewriteImages(doc: Document, options: RenderManualOptions): void {
  doc.querySelectorAll("img[src]").forEach((img) => {
    const src = img.getAttribute("src");
    if (!src) return;

    // An external image loads directly; it never went through the manual's
    // own path resolution or its authorisation.
    if (EXTERNAL_LINK.test(src)) return;

    img.removeAttribute("src");
    const resolved = resolveManualPath(options.currentPath, src);
    if (resolved === null) return;
    img.setAttribute("data-manual-src", resolved);
  });
}
